import { useCurrentAccount } from "@mysten/dapp-kit-react";
import { useEffect, useMemo } from "react";

import { config } from "@/config";
import { useSuiQueryContext } from "@/context/SUIQuery/useSuiQueryContext";
import { useSuiTransactions } from "@/hooks/sui/queries";

import { GetAssignCustomReferralCodeFunctionProps } from "./GetAssignCustomReferralCodeFunction.types";

// MIGRATION: JSON-RPC's `MoveFunction: { package, module, function }` filter
// becomes a single `::`-delimited Move path, and `options: { showEvents: true }`
// becomes `include: { events: true }`.
const ASSIGN_CUSTOM_REFERRAL_CODE = `${config.packageId}::swarm_license_sale::assign_custom_referral_code`;

const CREATE_REFERRER_EVENT = `${config.packageId}::swarm_license_sale::CreateReferrerEvent`;

export const GetAssignCustomReferralCodeFunction: React.FC<
  GetAssignCustomReferralCodeFunctionProps
> = () => {
  const account = useCurrentAccount();

  const { setKOLWalletsReferralCodeCheckObj } = useSuiQueryContext();

  const { data, isPending, error } = useSuiTransactions(
    {
      filter: { function: ASSIGN_CUSTOM_REFERRAL_CODE },
      include: { events: true },
    },
    { enabled: !!config.packageId },
  );

  const createReferrerEventsTargetWallets = useMemo(() => {
    if (!data?.transactions) return [];

    const KOLWallets = [];

    // MIGRATION: list items are a `{ $kind, Transaction | FailedTransaction }`
    // union now, and events expose `eventType` / `json` (was `type` /
    // `parsedJson`). Reverted transactions never emit events, so only successful
    // ones are inspected.
    for (const result of data.transactions) {
      const tx = result.Transaction;

      if (!tx?.events) continue;

      for (const event of tx.events) {
        if (event.eventType === CREATE_REFERRER_EVENT) {
          const wallet = (event.json as { wallet: string } | null)?.wallet;

          if (wallet) KOLWallets.push(wallet);
        }
      }
    }

    return KOLWallets;
  }, [data?.transactions]);

  useEffect(() => {
    if (
      !createReferrerEventsTargetWallets ||
      createReferrerEventsTargetWallets.length === 0 ||
      !account?.address
    )
      return;

    setKOLWalletsReferralCodeCheckObj({
      KOLWallets: createReferrerEventsTargetWallets,
      shouldSetGENESISCodeToConnectedAccount: createReferrerEventsTargetWallets.includes(
        account.address,
      ),
    });
  }, [createReferrerEventsTargetWallets, account]);

  if (error) {
    return null;
  }

  if (isPending) {
    return null;
  }

  return null;
};
